//core
import React, { useState, useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import { getSymbolFromCode } from "currency-code-symbol-map";
import { Box, IconButton, Typography } from "@mui/material";

//styles
import { useStyles } from "./styles";

//icons
import RefreshIcon from "@mui/icons-material/Refresh";

//hooks
import { useUser } from "../../../../../../../store/common/user/useUser";
import { useAgents as useAdminAgents } from "../../../../../../../store/admin/agents/useAgents";
import { useAgents } from "../../../../../../../store/agent/agents/useAgents";

//selectors
import { selectAgentNetworkBalanceData } from "../../../../../../../store/agent/agents/selectors";
import { selectAgentNetworkBalanceData as selectAdmAgentNetworkBalanceData } from "../../../../../../../store/admin/agents/selectors";

//components
import FloatFormat from "../../../FloatFormat";
import PermissionsWrapper from "../../../../../PermissionsWrapper";

interface INetworkBalanceItemProps {
  networkBalanceItem: any;
  agentId: number;
}

const NetworkBalanceItem: React.FC<INetworkBalanceItemProps> = ({
  networkBalanceItem,
  agentId,
}): React.ReactElement => {
  const styles = useStyles();

  const { userIsAdmin, userIsAgent } = useUser();
  const { getAgentNetworkBalanceById, clearAgentNetworkBalance } = useAgents();
  const adminAgents = useAdminAgents();

  const agentNetworkBalance = useSelector(selectAgentNetworkBalanceData);
  const adminNetworkBalance = useSelector(selectAdmAgentNetworkBalanceData);

  const [currentBalance, setCurrentBalance] = useState<string>(networkBalanceItem.balanceAmount);

  const permissions = userIsAdmin
    ? ["api3.admin.agentcontroller.network_balance_show"]
    : ["api3.agent.agentcontroller.network_balance_show"];

  useEffect(() => {
    if (!!agentNetworkBalance) {
      if (agentNetworkBalance.agentId === agentId) {
        const updatedItem = agentNetworkBalance.networkBalance.find(
          (item: any) => item.currency === networkBalanceItem.currency
        );
        if (updatedItem) {
          setCurrentBalance(updatedItem.balanceAmount);
        }
        clearAgentNetworkBalance();
      }
    }
  }, [agentNetworkBalance, agentId, networkBalanceItem.currency, clearAgentNetworkBalance]);

  useEffect(() => {
    if (!!adminNetworkBalance) {
      if (adminNetworkBalance.agentId === agentId) {
        const updatedItem = adminNetworkBalance.networkBalance.find(
          (item: any) => item.currency === networkBalanceItem.currency
        );
        if (updatedItem) {
          setCurrentBalance(updatedItem.balanceAmount);
        }
        adminAgents.clearAgentNetworkBalance();
      }
    }
  }, [adminNetworkBalance, agentId, networkBalanceItem.currency, adminAgents]);

  const handleGetBalance = useCallback(() => {
    if (userIsAgent) {
      getAgentNetworkBalanceById({ agentId: agentId });
    }
    if (userIsAdmin) {
      adminAgents.getAgentNetworkBalanceById({ agentId: agentId });
    }
  }, [userIsAgent, userIsAdmin, agentId, adminAgents, getAgentNetworkBalanceById]);

  return (
    <Box className={styles.block}>
      <PermissionsWrapper permissions={permissions}>
        <IconButton
          className={styles.refresh}
          size="small"
          aria-label="refresh"
          onClick={handleGetBalance}
        >
          <RefreshIcon fontSize="small" />
        </IconButton>
      </PermissionsWrapper>

      <Typography className={`${styles.content} force-ltr`} component={"div"} variant="body2">
        {`${networkBalanceItem.currency} - `}
        {getSymbolFromCode(networkBalanceItem.currency) ? (
          <Box>{getSymbolFromCode(networkBalanceItem.currency)} </Box>
        ) : (
          networkBalanceItem.currency
        )}
        <Box>
          <FloatFormat value={currentBalance} />
        </Box>
      </Typography>
    </Box>
  );
};

export default NetworkBalanceItem;
